/**
 * Data plane — Dataset **declared metrics** (L2, ADR-0029) API client.
 *
 * A metric is the BI agent's "CLAUDE.md" for a dataset: declared once (a name +
 * an aggregation over a field, an optional always-AND filter + suggested
 * dimensions), the Ask / app assistant reads it as ground truth instead of
 * re-inferring "fatturato" / "clienti attivi" from raw column names every turn.
 *
 * Mirrors the metric endpoints on `backend/src/hub/routes/datasets.py`
 * (`/api/admin/datasets/{id}/metrics`, gated `semantics:read` / `:write`). The
 * aggregation vocabulary comes from the shared semantics `vocab` endpoint
 * (`SemanticsVocab.aggregations`).
 */
import { api } from "./api";
import type { DatasetFilterSpec } from "./api-datasets";

/** The create/update body — exactly what the agent reads (SQL-free). */
export interface DatasetMetricInput {
  name: string;
  description?: string | null;
  /** ∈ `SemanticsVocab.aggregations` (sum/avg/count/count_distinct/min/max). */
  aggregation_fn: string;
  /** The aggregated column. NULL only for `count` (= count(*)). */
  field?: string | null;
  /** Optional always-AND WHERE — the slice the metric counts over. */
  filter?: DatasetFilterSpec | null;
  /** Suggested group-bys the agent can offer ("per agente", "per mese"). */
  default_dimensions?: string[];
}

/** A stored metric as returned by the API. */
export interface DatasetMetric extends DatasetMetricInput {
  id: string;
  field: string | null;
  filter: DatasetFilterSpec | null;
  default_dimensions: string[];
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export const datasetMetricsApi = {
  list: (datasetId: string) =>
    api.get<DatasetMetric[]>(`/datasets/${datasetId}/metrics`),

  create: (datasetId: string, body: DatasetMetricInput) =>
    api.post<DatasetMetric>(`/datasets/${datasetId}/metrics`, body),

  update: (datasetId: string, metricId: string, body: DatasetMetricInput) =>
    api.put<DatasetMetric>(`/datasets/${datasetId}/metrics/${metricId}`, body),

  remove: (datasetId: string, metricId: string) =>
    api.delete<void>(`/datasets/${datasetId}/metrics/${metricId}`),

  /** AI bootstrap — Odino drafts metrics for review (ADR-0029). Nothing is saved;
   * the operator picks which to create. 422 if no LLM provider is configured. */
  propose: (datasetId: string) =>
    api.post<DatasetMetricInput[]>(`/datasets/${datasetId}/metrics/propose`, {}),
};
